import React from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { createClient } from '@/utils/supabase/client';

import HomePage from '@/pages/Home';
import GamesArcadePage from '@/pages/Games';
import CreatorPage from '@/pages/Creator';
import AdminUsagePage from '@/pages/AdminUsage';

import AuthLayout from '@/pages/auth/AuthLayout';
import LoginPage from '@/pages/auth/Login';
import SignupPage from '@/pages/auth/Signup';
import ForgotPasswordPage from '@/pages/auth/ForgotPassword';
import ResetPasswordPage from '@/pages/auth/ResetPassword';
import CheckEmailPage from '@/pages/auth/CheckEmail';
import CheckResetEmailPage from '@/pages/auth/CheckResetEmail';
import AuthErrorPage from '@/pages/auth/AuthError';
import CallbackPage from '@/pages/authcb/Callback';
import ConfirmPage from '@/pages/authcb/Confirm';

import LegalPage from '@/pages/Legal';
import OnboardingPage from '@/pages/Onboarding';
import SettingsPage from '@/pages/Settings';
import GuidePage from '@/pages/Guide';
import NotFoundPage from '@/pages/NotFound';

import { UXStateProvider } from '@/context/UXStateContext';
import UXStateModal from '@/components/ui/UXStateModal';
import FloatingVideoAd from '@/components/FloatingVideoAd';
import OnboardingModal from '@/components/OnboardingModal';
import InteractiveAppTour from '@/components/InteractiveAppTour';

const withAuthShell = (Page) => (
  <AuthLayout>
    <Page />
  </AuthLayout>
);

function AuthHashListener() {
  const navigate = useNavigate();

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleHash = async () => {
      const hash = window.location.hash;
      if (!hash) return;

      const hashParams = new URLSearchParams(hash.replace(/^#/, ''));
      const error = hashParams.get('error');
      const errorCode = hashParams.get('error_code');
      const errorDescription = hashParams.get('error_description');

      if (error || errorCode) {
        window.history.replaceState(null, '', window.location.pathname);
        const resolvedError = errorCode || error || 'unknown';
        const msg = errorDescription || 'Authentication link is invalid or has expired.';
        navigate(`/auth-error?error=${encodeURIComponent(resolvedError)}&message=${encodeURIComponent(msg)}`);
        return;
      }

      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const type = hashParams.get('type');
      if (accessToken) {
        const supabase = createClient();
        const { data } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken || '',
        });
        const verifiedEmail =
          data?.user?.email ||
          localStorage.getItem('kheldrop_last_signup_email') ||
          '';
        if (verifiedEmail) {
          localStorage.setItem('kheldrop_verified_email', verifiedEmail);
        }
        if (data?.user) {
          localStorage.setItem('kheldrop_auth_user', JSON.stringify(data.user));
          if (data.session) localStorage.setItem('kheldrop_token', data.session.access_token);
        }
        window.history.replaceState(null, '', window.location.pathname);
        if (
          type === 'signup' ||
          type === 'email_verification' ||
          type === 'email_change' ||
          window.location.hash.includes('type=signup')
        ) {
          navigate(`/login?verified=true&email=${encodeURIComponent(verifiedEmail)}`);
        } else {
          navigate('/?verified=true');
        }
      }
    };

    handleHash();
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, [navigate]);

  return null;
}

function App() {
  return (
    <UXStateProvider>
      <BrowserRouter>
        <AuthHashListener />
        <UXStateModal />
        <OnboardingModal />
        <InteractiveAppTour />
        <FloatingVideoAd title="KHELDROP" />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/games" element={<GamesArcadePage />} />
          <Route path="/creator" element={<CreatorPage />} />
          <Route path="/admin/usage" element={<AdminUsagePage />} />

          {/* Onboarding, Guide & Settings */}
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/guide" element={<GuidePage />} />
          <Route path="/how-to-play" element={<GuidePage />} />
          <Route path="/playbook" element={<GuidePage />} />
          <Route path="/how-it-works" element={<GuidePage />} />

          {/* Legal, Policies & Trust Hub */}
          <Route path="/legal" element={<LegalPage defaultDoc="terms" />} />
          <Route path="/privacy" element={<LegalPage defaultDoc="privacy" />} />
          <Route path="/privacy-policy" element={<LegalPage defaultDoc="privacy" />} />
          <Route path="/terms" element={<LegalPage defaultDoc="terms" />} />
          <Route path="/terms-of-service" element={<LegalPage defaultDoc="terms" />} />
          <Route path="/community" element={<LegalPage defaultDoc="community" />} />
          <Route path="/community-guidelines" element={<LegalPage defaultDoc="community" />} />
          <Route path="/safety" element={<LegalPage defaultDoc="safety" />} />
          <Route path="/age-policy" element={<LegalPage defaultDoc="age-policy" />} />
          <Route path="/age-limit" element={<LegalPage defaultDoc="age-policy" />} />
          <Route path="/minors" element={<LegalPage defaultDoc="age-policy" />} />
          <Route path="/location-privacy" element={<LegalPage defaultDoc="location-privacy" />} />
          <Route path="/grievance" element={<LegalPage defaultDoc="grievance" />} />
          <Route path="/copyright" element={<LegalPage defaultDoc="copyright" />} />
          <Route path="/cookies" element={<LegalPage defaultDoc="cookies" />} />
          <Route path="/cookie-policy" element={<LegalPage defaultDoc="cookies" />} />
          <Route path="/cookie-preferences" element={<LegalPage defaultDoc="preferences" />} />
          <Route path="/disclaimer" element={<LegalPage defaultDoc="disclaimer" />} />
          <Route path="/accessibility" element={<LegalPage defaultDoc="accessibility" />} />
          <Route path="/accessibility-statement" element={<LegalPage defaultDoc="accessibility" />} />
          <Route path="/dpa" element={<LegalPage defaultDoc="dpa" />} />
          <Route path="/acceptable-use" element={<LegalPage defaultDoc="acceptable-use" />} />
          <Route path="/security" element={<LegalPage defaultDoc="security" />} />
          <Route path="/security-policy" element={<LegalPage defaultDoc="security" />} />
          <Route path="/disclosure" element={<LegalPage defaultDoc="disclosure" />} />
          <Route path="/responsible-disclosure" element={<LegalPage defaultDoc="disclosure" />} />
          <Route path="/help" element={<LegalPage defaultDoc="help" />} />
          <Route path="/support" element={<LegalPage defaultDoc="help" />} />
          <Route path="/help-center" element={<LegalPage defaultDoc="help" />} />

          {/* Auth routes */}
          <Route path="/login" element={withAuthShell(LoginPage)} />
          <Route path="/signup" element={withAuthShell(SignupPage)} />
          <Route path="/forgot-password" element={withAuthShell(ForgotPasswordPage)} />
          <Route path="/reset-password" element={withAuthShell(ResetPasswordPage)} />
          <Route path="/check-email" element={withAuthShell(CheckEmailPage)} />
          <Route path="/check-reset-email" element={withAuthShell(CheckResetEmailPage)} />
          <Route path="/auth-error" element={withAuthShell(AuthErrorPage)} />

          <Route path="/auth/callback" element={<CallbackPage />} />
          <Route path="/auth/confirm" element={<ConfirmPage />} />

          {/* 404 Catch-all */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
    </UXStateProvider>
  );
}

export default App;
